import { Box, TableCell, TableRow } from '@material-ui/core';
import { FeeType, formatMoney, formatPercent, Pricing, PricingRange, Product } from '@nemuru-eng/js-api-client';

import { MoreActionsMenuWithIcon } from '@/components/shared/components/MoreActionsMenu/MoreActionsMenuWithIcon';
import { ActionsOption } from '@/components/shared/components/MoreActionsMenu/types/ActionsOption';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { Font } from '@/components/shared/components/Font';
import { Text } from '@/components/shared/components/Text';
import { useNotificationsContext } from '@/contexts/NotificationsContext/NotificationsContext';
import { useCustomTheme } from '@/hooks/useCustomTheme';

import { usePricingSpecsTable } from '../hooks/usePricingSpecsTable';
import { PricingSpecsTableAction } from '../types/PricingSpecsTableAction';
import { LenderEarningFee } from './LenderEarningFee';
import { PricingInsuranceChip } from '@/components/shared/components/PricingsTable/components/PricingInsuranceChip';

interface PricingSpecTableRowProps {
  pricing: Pricing;
  pricingRange: PricingRange;
  editModeAllowed?: boolean;
  handleRowPricingAction: (action: PricingSpecsTableAction, pricingRange: PricingRange) => void;
}

export const PricingSpecTableRow = ({
  pricing,
  pricingRange,
  editModeAllowed,
  handleRowPricingAction,
}: PricingSpecTableRowProps) => {
  const {
    actions: { setSnackbarNotification },
  } = useNotificationsContext();

  const { getTextIdTranslation } = useTranslation();
  const { palette } = useCustomTheme();
  const { classes } = usePricingSpecsTable(pricing);

  // const missingLenders = getLendersWithoutLenderEarningFees(lenders, pricingRange.lenderEarningsFees);

  const actions: ActionsOption[] = editModeAllowed
    ? [
        {
          display: <Text tId={'COPY_PRICING_SPEC_ID'} />,
          iconName: 'copy',
          onClick: () => {
            navigator.clipboard.writeText(pricingRange.id);
            setSnackbarNotification({
              msg: 'COPY_SUCCESSFUL',
              level: 'success',
            });
          },
        },
        {
          display: <Text tId={'ADD_PRICING_SPEC_IN_LENDER_CONTEXT'} />,
          iconName: 'add',
          onClick: () =>
            handleRowPricingAction(PricingSpecsTableAction.ADD_PRICING_SPEC_IN_LENDER_CONTEXT, pricingRange),
          // disabled: missingLenders.length === 0,
        },
        {
          display: <Text tId={'REMOVE_PRICING_SPEC_IN_LENDER_CONTEXT'} />,
          iconName: 'remove',
          onClick: () =>
            handleRowPricingAction(PricingSpecsTableAction.REMOVE_PRICING_SPEC_IN_LENDER_CONTEXT, pricingRange),
          disabled: !pricingRange.lenderEarningsFees || pricingRange.lenderEarningsFees.length === 0,
        },
        {
          display: <Text tId={'REMOVE_PRICING_SPEC_FROM_PRICING'} />,
          iconName: 'delete',
          onClick: () => handleRowPricingAction(PricingSpecsTableAction.REMOVE_PRICING_SPEC_FROM_PRICING, pricingRange),
          disabled: pricing.ranges.length === 1,
        },
      ]
    : [];

  const setupFee = pricingRange.setupFee;
  const merchantFee = pricingRange.merchantFee;

  return (
    <>
      <TableRow key={pricingRange.id} className={classes.root}>
        <TableCell align={'left'}>
          <Box display={'flex'} minWidth={'200px'}>
            <Font variant={'body2'} color={'textPrimary'}>
              {formatMoney({ value: pricingRange.rangePrincipalMin, currency: pricing.currency }, 2)}
              {' - '}
              {formatMoney({ value: pricingRange.rangePrincipalMax, currency: pricing.currency }, 2)}
            </Font>
          </Box>
        </TableCell>
        <TableCell align={'left'}>
          <Box display={'flex'} minWidth={'80px'}>
            <Font variant={'body2'} color={'textPrimary'}>
              {pricingRange.rangePeriodMin}
              {' - '}
              {pricingRange.rangePeriodMax}
            </Font>
          </Box>
        </TableCell>
        <TableCell align={'left'}>
          <Box display={'flex'} minWidth={'60px'}>
            <Font variant={'body2'} color={'textPrimary'}>
              {pricingRange.annualInterest !== null ? formatPercent(pricingRange.annualInterest) : ''}
            </Font>
          </Box>
        </TableCell>
        <TableCell align={'left'}>
          <Box display={'flex'} minWidth={'120px'}>
            <Font variant={'body2'} color={'textPrimary'}>
              {setupFee.type === FeeType.FIX_AMOUNT
                ? formatMoney({ value: setupFee.value, currency: pricing.currency })
                : formatPercent(setupFee.value)}{' '}
              <Text tId={setupFee.paymentType} />
            </Font>
          </Box>
        </TableCell>
        <TableCell align={'left'}>
          <Box display={'flex'} minWidth={'120px'}>
            <Font variant={'body2'} color={'textPrimary'}>
              {pricingRange.merchantDiscountPercentage !== null
                ? formatPercent(pricingRange.merchantDiscountPercentage, 2)
                : ''}
            </Font>
          </Box>
        </TableCell>
        <TableCell align={'left'}>
          <Box display={'flex'} minWidth={'80px'}>
            <Font variant={'body2'} color={'textPrimary'}>
              {merchantFee?.value !== null
                ? merchantFee?.type === FeeType.FIX_AMOUNT
                  ? formatMoney({ value: merchantFee.value, currency: pricing.currency })
                  : merchantFee?.value
                  ? formatPercent(merchantFee.value)
                  : formatPercent(0)
                : ''}
            </Font>
          </Box>
        </TableCell>
        <TableCell align={'left'}>
          <Box display={'flex'} minWidth={'120px'}>
            <Font variant={'body2'} color={'textPrimary'}>
              {pricingRange.gracePercentage !== null ? formatPercent(pricingRange.gracePercentage, 2) : ''}
              {pricingRange.gracePeriod !== null ? ` (${pricingRange.gracePeriod})` : ''}
            </Font>
          </Box>
        </TableCell>
        <TableCell align={'left'}>
          <Box display={'flex'} minWidth={'120px'}>
            <Font variant={'body2'} color={'textPrimary'}>
              {pricingRange.paymentMethodType ? <Text tId={pricingRange.paymentMethodType} /> : ''}
            </Font>
          </Box>
        </TableCell>
        <TableCell align={'left'}>
          <Box display={'flex'} minWidth={'120px'}>
            {pricingRange.lenderEarningsFees
              ?.sort((a, b) => (a.lenderId > b.lenderId ? 1 : -1))
              .map((lenderEarningFee) => {
                return (
                  <LenderEarningFee
                    key={`${pricingRange.id}-${pricingRange.id}`}
                    pricingSpecInLenderContext={lenderEarningFee}
                    palette={palette}
                    classes={classes}
                  />
                );
              })}
          </Box>
        </TableCell>
        {pricing.product === Product.AUTO && (
          <>
            <TableCell align={'left'}>
              <Box display={'flex'} minWidth={'160px'} flexWrap={'wrap'} gridGap={4}>
                {pricingRange.availableInsurances.map((insurance) => (
                  <PricingInsuranceChip key={`${insurance.type}-${insurance.provider}`} insurance={insurance} />
                ))}
                {pricingRange.availableInsurances.length === 0 && (
                  <Font variant={'body2'} color={'textPrimary'}>
                    -
                  </Font>
                )}
              </Box>
            </TableCell>
            <TableCell align={'left'}>
              <Box display={'flex'} minWidth={'160px'}>
                <Font variant={'body2'} color={'textPrimary'}>
                  {pricingRange.yearsSinceAutoRegistrationMin !== null
                    ? `${pricingRange.yearsSinceAutoRegistrationMin} `
                    : ''}
                  {'-'}
                  {pricingRange.yearsSinceAutoRegistrationMax !== null
                    ? ` ${pricingRange.yearsSinceAutoRegistrationMax}`
                    : ''}
                </Font>
              </Box>
            </TableCell>
          </>
        )}
        <TableCell align={'left'}>
          <MoreActionsMenuWithIcon actionsOptionsArray={actions} />
        </TableCell>
      </TableRow>
    </>
  );
};
